import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  onClose: () => void;
}

interface State {
  error: Error | null;
}

/**
 * Isolates any crash inside voice-conversation mode (VAD/ONNX/Web Audio are
 * all genuinely fragile browser APIs — see docs/VOICE-5-conversation-mode.md
 * for real failures hit during testing) from the rest of the chat app.
 * Without this, an uncaught error anywhere in the voice tree unmounts the
 * ENTIRE React app (confirmed live during testing — the whole chat screen
 * went blank, not just the voice overlay). A feature failing should never
 * take the core chat down with it.
 */
export class VoiceConversationErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-[var(--color-paper)] text-[var(--color-ink)] px-6 text-center"
          role="alertdialog"
          aria-modal="true"
          aria-label="Voice conversation error"
        >
          <p className="text-[15px] font-semibold">Voice conversation hit a snag</p>
          <p className="text-[13px] text-[var(--color-ink-40)] max-w-[380px]">
            Something went wrong starting the voice session. Your chat is unaffected — you can
            keep typing as normal.
          </p>
          <button
            type="button"
            onClick={this.props.onClose}
            className="inline-flex items-center h-10 px-5 rounded-full bg-[var(--color-ink-08)] text-[var(--color-ink-80)] text-[13px] font-semibold hover:bg-[var(--color-ink-16)]"
          >
            Close
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default VoiceConversationErrorBoundary;
